import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from 'src/features/auth/AuthContext';
import { getMembers } from 'src/features/projects/api';
import { useProjectAccessCtx } from 'src/features/projects/ProjectAccessContext';
import { hasPerm } from 'src/features/projects/hooks/useProjectAccess';

import { useTasks } from '../hooks/useTasks';
import { useCreateTask } from '../hooks/useCreateTask';
import { useUpdateTask } from '../hooks/useUpdateTask';
import { useDeleteTask } from '../hooks/useDeleteTask';

import TaskListForm from './TaskListForm';
import type { Member } from 'src/features/projects/types';

export default function TaskListContainer({ projectId }: { projectId: string }) {
    const { me } = useAuth();
    const currentUserId = me?.id;

    // 권한
    const { access, isLoading: accessLoading } = useProjectAccessCtx();
    const canEditTask = useMemo(() => !accessLoading && hasPerm(access?.permissions, 'task.edit'), [accessLoading, access]);

    // 멤버 → name/email map
    const membersQ = useQuery({
        queryKey: ['projects', 'members', projectId],
        queryFn: () => getMembers(projectId).then((r) => r.data),
        staleTime: 60_000,
    });

    const userNameMap = useMemo(() => {
        const list = (membersQ.data ?? []) as Member[];
        const map: Record<string, string> = {};
        for (const m of list) {
            const uid = m.userId;
            const name = (m as any).user?.name ?? (m as any).userName ?? '';
            const email = (m as any).user?.email ?? (m as any).userEmail ?? '';
            map[uid] = name && email ? `${name} <${email}>` : name || email || uid;
        }
        return map;
    }, [membersQ.data]);

    // Task list
    const tasksQ = useTasks(projectId, { includeInactive: true, includeDone: true });
    const createM = useCreateTask(projectId);
    const updateM = useUpdateTask(projectId);
    const deleteM = useDeleteTask(projectId);

    return (
        <div className="w-full space-y-6">
            <TaskListForm
                projectId={projectId}
                tasks={tasksQ.data ?? []}
                loading={tasksQ.isLoading}
                canEdit={canEditTask}
                currentUserId={currentUserId}
                userNameMap={userNameMap}
                onCreate={(input) => createM.mutateAsync(input)}
                onUpdate={(taskId, patch) => updateM.mutateAsync({ taskId, patch })}
                onDelete={(taskId) => deleteM.mutateAsync(taskId)}
                onReload={() => tasksQ.refetch()}
            />
        </div>
    );
}
